import React, { useEffect, useState, useRef } from 'react';
import BackgroundCollage from './BackgroundCollage.js';
import styled, { withTheme } from 'styled-components';
import {TRANSITION_CLASS_LEFT, TRANSITION_CLASS_RIGHT, TRANSITION_DURATION, ENTRANCE_CLASS_LEFT, ENTRANCE_CLASS_RIGHT} from './carousel-constants.js'

/* Separators */
import MahabalipuramSeparator from '../../../assets/img/carousel/separators/mahabalipuram.png';
import MysoreSeparator from '../../../assets/img/carousel/separators/mysore.png';
import NainitalSeparator from '../../../assets/img/carousel/separators/nainital.png';

/* City title images */
import MahabalipuramTitle from '../../../assets/img/carousel/titles/mahabalipuram.png';
import MysoreTitle from '../../../assets/img/carousel/titles/mysore.png';
import NainitalTitle from '../../../assets/img/carousel/titles/nainital.png';

import MysoreCircle from '../../../assets/img/carousel/Mysore/8-circle.png';
import NainitalCircle from '../../../assets/img/carousel/Nainital/circle.png';
import MahabalipuramCircle from '../../../assets/img/carousel/Mysore/8-circle.png';

import IncredibleIndiaLogo from '../../../assets/img/carousel/incredible-india-logo.png';
import useElementInertialParallax from '../../hooks/useElementInertialParallax.js';

function transitionsAnimation(options) {
    const { duration, distance = '40%', timingFunction = 'ease' } = options
    const hash = options.name || ''

    return `
    &.${TRANSITION_CLASS_LEFT} {
        animation: leaving-left-${hash} ${duration}s ${timingFunction};
        transform: translate(${distance});
    }
    &.${TRANSITION_CLASS_RIGHT} {
        animation: leaving-right-${hash} ${duration}s ${timingFunction};
        transform: translate(-${distance});
    }
    &.${ENTRANCE_CLASS_RIGHT} {
        animation: entrance-right-${hash} ${duration}s ${timingFunction};
    }
    &.${ENTRANCE_CLASS_LEFT} {
        animation: entrance-left-${hash} ${duration}s ${timingFunction};
    }

    @keyframes leaving-left-${hash} {
        from {
            transform: translate(0);
        }
        to {
            transform: translate(${distance});
        }
    }

    @keyframes leaving-right-${hash} {
        from {
            transform: translate(0);
        }
        to {
            transform: translate(-${distance});
        }
    }

    @keyframes entrance-right-${hash} {
        from {
            transform: translate(${distance});
        }
        to {
            transform: translate(0);
        }
    }

    @keyframes entrance-left-${hash} {
        from {
            transform: translate(-${distance});
        }
        to {
            transform: translate(0);
        }
    }
`;
}

const TitleContainer = styled.div`
    position: absolute;
    display: ${props => (props.header || props.footer ) ? 'block' : (props.displayData.visible !== 'none' ? 'block' : props.displayData.visible)};
    z-index: ${props => props.header ? '5000' : props.displayData.zIndex};

    width: 100%;
    height: 100%;
    overflow: hidden;
    pointer-events: none;
`;

const StyledSlide = styled.div`
    position: absolute;
    display: ${props => props.header || props.footer ? 'flex' : props.displayData.visible};
    align-items: center;
    justify-content: center;
    background-color: ${props => props.backgroundColor};
    background-image: url(${props => props.circle});
    background-position: center;
    background-repeat: no-repeat;
    transition: clip-path 1s;
    z-index: ${props => props.header ? '5000' : props.displayData.zIndex};

    width: 100%;
    height: 100%;

    &.${TRANSITION_CLASS_LEFT},
    &.${TRANSITION_CLASS_RIGHT} {
        clip-path: url("#mask");
        -webkit-clip-path: url("#mask"); 
    }

    @media (max-width: ${props => props.theme.pageWidth.s}px) {
        background-size: cover;
    }
`;

const WelcomeContainer = styled.div`
    margin: auto;
    display: flex;
    width: 30%;
    align-items: center;
    height: 1rem;

    @media (max-width: ${props => props.theme.pageWidth.s}px) {
        width: 80%;
    }
`;

const WelcomeText = styled.p`
    text-align: center;
    text-transform: uppercase;
    color: ${props => props.color};
    font-family: ${props => props.theme.fonts.display};
    font-size: 1rem;
    white-space: nowrap;
`;


const borderCircle = `
    position: absolute;
    content: " ";
    display:block;
    width:5px;
    height:5px;
    border-radius: 50%;
`;

const Border = styled.div`
    position: relative;
    height: 1px;
    background:${props => `${props.color}50`};
    width: 20%;
    display:flex;
    margin:0 auto;
    align-self: center;
    align-items: center;

    &::before {
        ${props => props.left ? borderCircle : ''}
        ${props => props.left ? `background-color:${props.color};`: ''}
    }

    &::after {
        ${props => props.right ? borderCircle : ''}
        ${props => props.right ? `background-color:${props.color};`: ''}
        ${props => props.right ? `right: 0;`: ''}
    }
`;

const IncredibleIndia = styled.img`
    margin: 1.5rem auto 2rem auto;
    height: 2rem;
    ${props => props.hide ? 'display: none;' : ''}

    @media (max-width: ${props => props.theme.pageWidth.s}px) {
        margin: 1.5rem auto 1.5rem auto;
    }
`;

const Separator = styled.img`
    margin: 1rem auto;
    display: block;

    @media (max-width: ${props => props.theme.pageWidth.s}px) {
        margin: .75rem auto;
    }
`;

const SectionTitle = styled.img`
    position: relative;
    margin: 1.25rem auto;
    display: block;
    max-width: 80%;
    top: 30vh;
    height: auto;
    

    @media (max-width: ${props => props.theme.pageWidth.m}px) {
        top: 25vh;
    }

    @media (min-width: ${props => props.theme.pageWidth.m}px) {
        top: 25vh;
    }
    ${transitionsAnimation({duration: TRANSITION_DURATION, distance: '100vw', timingFunction: 'cubic-bezier(.65,.1,.25,1)', name: 'title'})}

    z-index: 8000;
`;

const AnimatedCollage = styled(BackgroundCollage)`
    ${transitionsAnimation({duration: TRANSITION_DURATION, distance: '25%', timingFunction: 'cubic-bezier(.65,0,.45,1)', name: 'collage'})}
`;

const HeaderContainer = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    display: flex;
    flex-direction: column;
`;

const CarouselIntro = styled.div`
    opacity: 1.0;
    height: 100px;
    transition: opacity 300ms ease-out, height 400ms ease-in;

    ${props => props.footer ? 'margin-top: 2rem;' : ''}
    ${props => props.fadeout ? 'opacity: 0; height: 0;' : ''}
    ${props => props.hide ? 'display: none; ' : ''}

`;

const Slide = (props) => {
    const cityStyles = {
        'Mahabalipuram': {
            title: MahabalipuramTitle,
            separator: MahabalipuramSeparator,
            backgroundColor: props.theme.colors.header1,
            textColor: props.theme.colors.headerText1,
            borderColor: props.theme.colors.headerHighlight1,
            circle: MahabalipuramCircle
        },
        'Nainital': {
            title: NainitalTitle,
            separator: NainitalSeparator,
            backgroundColor: props.theme.colors.header2,
            textColor: props.theme.colors.headerText2,
            borderColor: props.theme.colors.headerHighlight2,
            circle: NainitalCircle
        },
        'Mysore': {
            title: MysoreTitle,
            separator: MysoreSeparator,
            backgroundColor: props.theme.colors.header3,
            textColor: props.theme.colors.headerText3,
            borderColor: props.theme.colors.headerHighlight3,
            circle: MysoreCircle
        },
    }
    const titleParallaxElement = useRef(null)
    const [titleParallaxOffset, setTitleParallaxOffset] = useState(0)
    
    useElementInertialParallax((parallaxValue) => {
        setTitleParallaxOffset(-20 + parallaxValue * 70)
    }, titleParallaxElement)

    return (
        <div>
            <StyledSlide    header={props.header} 
                            displayData={props.displayData} 
                            className={props.displayData.transitionClass} 
                            backgroundColor={cityStyles[props.displayData.name].backgroundColor}
                            circle={cityStyles[props.displayData.name].circle}
                            footer={props.footer}>
                <HeaderContainer>
                    <IncredibleIndia hide={props.footer} src={IncredibleIndiaLogo} alt="Incredible India"/>
                    <CarouselIntro fadeout={props.fadeout} hide={props.header} footer={props.footer}>
                        <Separator src={cityStyles[props.displayData.name].separator}></Separator>
                        <WelcomeContainer>
                            <Border left={true} right={false} color={cityStyles[props.displayData.name].borderColor}></Border>
                            <WelcomeText color={cityStyles[props.displayData.name].textColor}>Welcome to</WelcomeText>
                            <Border right={true} left={false} color={cityStyles[props.displayData.name].borderColor}></Border>
                        </WelcomeContainer>
                    </CarouselIntro>
                </HeaderContainer>
                <AnimatedCollage    transitionClassName={props.displayData.transitionClass} 
                                    city={props.displayData.name}></AnimatedCollage>
            </StyledSlide>
            <TitleContainer displayData={props.displayData} header={props.header} footer={props.footer}>
                <SectionTitle   src={cityStyles[props.displayData.name].title} 
                                alt={props.displayData.name} 
                                className={props.displayData.transitionClass}></SectionTitle>
            </TitleContainer>
        </div>
    );
};

export default withTheme(Slide);